import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CapabilitySummary } from "@/components/connectors/capability-browser";
import {
	AuthStatusBadge,
	ConnectMenu,
} from "@/components/connectors/connect-menu";
import {
	AddConnectionButton,
	ConnectorConnectionsBrowser,
} from "@/components/connectors/connections-browser";
import {
	ConnectorDisplay,
	ConnectorMark,
	extractOAuthDomain,
	resolveConnectorDisplay,
} from "@/components/connectors/connector-display";
import { ConnectorFamilyDetail } from "@/components/connectors/connector-family-detail";
import {
	ConnectorPageColumn,
	ConnectorPageShell,
} from "@/components/connectors/connector-page-shell";
import { ConnectorVersionBadge } from "@/components/connectors/connector-provenance";
import {
	DETAIL_CRUMB_LINK_CLASS,
	DetailBreadcrumb,
	DetailHeader,
} from "@/components/connectors/detail-header";
import { FacetChips } from "@/components/connectors/facet-chips";
import { PageSection } from "@/components/connectors/page-section";
import {
	getAppInstallMethod,
	getOAuthMethod,
	getSelectableMethods,
} from "@/components/entity-tabs/connections-tab/auth-helpers";
import { getAuthMethods } from "@/components/entity-tabs/connections-tab/auth-methods";
import { CreateProviderForm } from "@/components/inference-providers/create-provider-form";
import {
	InferenceProviderDetail,
	InferenceProviderRemoveSection,
} from "@/components/inference-providers/provider-detail";
import { OwnerWorkspaceGate } from "@/components/owner-workspace-gate";
import { useNotificationPeek } from "@/components/peek/notification-peek-context";
import { RunActivityLink } from "@/components/runs/run-activity-link";
import {
	SandboxDetail,
	SandboxOverview,
} from "@/components/sandboxes/sandbox-detail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { useMembers } from "@/hooks/use-members";
import {
	type AuthProfileItem,
	type ConnectionItem,
	type ConnectorDefinitionItem,
	useAuthProfiles,
	useConnections,
	useInstallableConnectorCatalog,
	useUninstallConnector,
} from "@/lib/api/connections";
import { useWorkspaceRoot } from "@/lib/api/entities";
import {
	type InferenceCatalogItem,
	isApiKeyAddable,
	supportsOAuth,
	useInferenceProviderCatalog,
	useInferenceProviders,
} from "@/lib/api/inference-providers";
import { useSandboxes } from "@/lib/api/sandboxes";
import { connectorIsDeviceBound } from "@/lib/connectors/facets";
import { INFERENCE_ROW_KEY_PREFIX } from "@/lib/connectors/inference-rows";
import {
	isRealConnectorKey,
	resolveConnectorRowKind,
} from "@/lib/connectors/row-kinds";
import {
	SANDBOX_PROVIDER_KEY_PREFIX,
	sandboxProviderFaviconDomain,
	sandboxProviderLabel,
} from "@/lib/connectors/sandbox-rows";
import { pruneSearch } from "@/lib/router-search";
import { cn, FILLED_BOX } from "@/lib/utils";

/** A provider row's facets: it serves inference and nothing else. */
const INFERENCE_ONLY_FACETS = {
	data: false,
	chat: false,
	actions: false,
	audience: false,
	inference: true,
	runtime: false,
} as const;

/** A sandbox row's facets: remote execution only. */
const RUNTIME_ONLY_FACETS = {
	data: false,
	chat: false,
	actions: false,
	audience: false,
	inference: false,
	runtime: true,
} as const;

export const Route = createFileRoute("/$owner/connectors/$connectorKey/")({
	validateSearch: (search: Record<string, unknown>) =>
		pruneSearch({
			// Provider targeting from server-emitted CTAs: `model` opens the
			// provider's edit controls, `reason` (presence-only) shows the agent
			// explainer, `agentId` names the agent in it.
			addAccount:
				search.addAccount === true || search.addAccount === "true"
					? true
					: undefined,
			instances:
				search.instances === true || search.instances === "true"
					? true
					: undefined,
			setup: search.setup === "oauth_app" ? ("oauth_app" as const) : undefined,
			model: typeof search.model === "string" ? search.model : undefined,
			reason: typeof search.reason === "string" ? search.reason : undefined,
			agentId: typeof search.agentId === "string" ? search.agentId : undefined,
		}),
	// Fire-and-forget prefetch — workspace warms the cache, the page mounts
	// on the first frame and ConnectorBody renders its own skeleton while
	// workspace + connector defs resolve. The connector-defs/connections
	// prefetches can only fire once workspace lands (they need orgId), so we
	// chain them via `.then` instead of awaiting — all happen, none block paint.
	loader: ({ context: { queryClient }, params }) => {
		void queryClient
			.fetchQuery(
				useWorkspaceRoot.queryOptions(params.owner, { slug: params.owner }),
			)
			.then((ws) => {
				const orgId = ws?.workspace?.id;
				// Provider keys resolve against the org's providers + the inference
				// catalog (their hooks scope off the URL), not connector defs.
				if (orgId && isRealConnectorKey(params.connectorKey)) {
					void queryClient.prefetchQuery(
						useInstallableConnectorCatalog.queryOptions(orgId),
					);
					void queryClient.prefetchQuery(
						useConnections.queryOptions(orgId, {
							connectorKey: params.connectorKey,
						}),
					);
				}
			})
			.catch(() => {
				// Page handles auth/missing-workspace state; swallow loader errors
				// so the global route error UI doesn't replace the page chrome.
			});
	},
	component: ConnectorIndexPage,
});

function ConnectorIndexPage() {
	const { owner, connectorKey } = Route.useParams();
	const { instances } = Route.useSearch();
	// A routable connector key can stand for a different source table entirely:
	// model providers, sandbox instances or kinds, and paired devices.
	// `resolveConnectorRowKind` is the single place that mapping lives.
	const { kind, id } = resolveConnectorRowKind(connectorKey);
	return (
		<OwnerWorkspaceGate owner={owner}>
			{({ ownerSlug, organizationId, isWorkspaceLoading }) => {
				if (instances && (kind === "inference" || kind === "sandbox-provider"))
					return (
						<ConnectorFamilyDetail
							ownerSlug={ownerSlug}
							familyKind={kind}
							familyId={id as string}
						/>
					);
				switch (kind) {
					case "inference":
						return (
							<ProviderConnectorBody
								ownerSlug={ownerSlug}
								providerSlug={id as string}
							/>
						);
					case "sandbox":
						return (
							<SandboxConnectorBody
								ownerSlug={ownerSlug}
								sandboxId={id as string}
							/>
						);
					case "sandbox-provider":
						return (
							<SandboxProviderConnectBody
								ownerSlug={ownerSlug}
								providerKind={id as string}
							/>
						);
					default:
						return (
							<ConnectorBody
								ownerSlug={ownerSlug}
								organizationId={organizationId}
								isWorkspaceLoading={isWorkspaceLoading}
								connectorKey={connectorKey}
							/>
						);
				}
			}}
		</OwnerWorkspaceGate>
	);
}

/**
 * A `sandbox-provider:{kind}` key is a CATALOG entry, not a thing that exists
 * yet — creating it is the "Add a connection" flow, which owns that page
 * chrome. Old deep links land here, so forward them instead of maintaining a
 * second credential form on the detail route.
 */
function SandboxProviderConnectBody({
	ownerSlug,
	providerKind,
}: {
	ownerSlug: string;
	providerKind: string;
}) {
	const navigate = useNavigate();
	useEffect(() => {
		void navigate({
			to: "/$owner/connectors/create/$connectorKey",
			params: {
				owner: ownerSlug,
				connectorKey: `${SANDBOX_PROVIDER_KEY_PREFIX}${providerKind}`,
			},
			replace: true,
		});
	}, [navigate, ownerSlug, providerKind]);
	return null;
}

/**
 * Sandbox instance detail on the shared connector route. Flat in CONTENT (a
 * credential + a status, one subject) but identical in CHROME to every other
 * connector: same shell, header and tab bar, with Feeds/Actions disabled.
 */
function SandboxConnectorBody({
	ownerSlug,
	sandboxId,
}: {
	ownerSlug: string;
	sandboxId: string;
}) {
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useSandboxes();
	const sandbox = data?.sandboxes.find((s) => s.id === sandboxId) ?? null;

	if (isLoading) {
		return (
			<ConnectorPageColumn className="space-y-4">
				<div className="flex items-center gap-2">
					<Skeleton className="h-6 w-6 rounded" />
					<Skeleton className="h-5 w-40" />
				</div>
				<Skeleton className="h-24 w-full rounded-lg" />
			</ConnectorPageColumn>
		);
	}

	if (isError)
		return (
			<ConnectorPageColumn>
				<div role="alert">
					<p>Could not load this instance.</p>
					<Button variant="outline" onClick={() => void refetch()}>
						Retry
					</Button>
				</div>
			</ConnectorPageColumn>
		);
	if (!sandbox) {
		return (
			<ConnectorPageColumn className="space-y-3">
				<p className="text-sm text-muted-foreground">
					This sandbox isn't in this workspace, so its details aren't available.
				</p>
				<Button
					variant="outline"
					size="sm"
					onClick={() =>
						void navigate({
							to: "/$owner/connectors",
							params: { owner: ownerSlug },
						})
					}
				>
					Back to connectors
				</Button>
			</ConnectorPageColumn>
		);
	}

	const displayName =
		sandbox.name || sandboxProviderLabel(sandbox.providerKind);

	return (
		<div className="flex flex-1 flex-col">
			<ConnectorPageShell
				header={
					<DetailHeader
						breadcrumb={
							<DetailBreadcrumb
								crumbs={[
									<Link
										key="connectors"
										to="/$owner/connectors"
										params={{ owner: ownerSlug }}
										className={DETAIL_CRUMB_LINK_CLASS}
									>
										Connectors
									</Link>,
									<Link
										key="family"
										to="/$owner/connectors/$connectorKey"
										params={{
											owner: ownerSlug,
											connectorKey: `${SANDBOX_PROVIDER_KEY_PREFIX}${sandbox.providerKind}`,
										}}
										search={{ instances: true }}
										className={DETAIL_CRUMB_LINK_CLASS}
									>
										{sandboxProviderLabel(sandbox.providerKind)}
									</Link>,
								]}
							/>
						}
						icon={
							<ConnectorMark
								name={displayName}
								faviconDomain={sandboxProviderFaviconDomain(
									sandbox.providerKind,
								)}
							/>
						}
						title={displayName}
						badge={
							<>
								<Badge variant="secondary">{sandbox.providerKind}</Badge>
								{sandbox.connected ? (
									<Badge className="border-transparent bg-emerald-500/10 text-emerald-500">
										Connected
									</Badge>
								) : (
									<Badge variant="destructive">No credential</Badge>
								)}
							</>
						}
						status={<FacetChips facets={RUNTIME_ONLY_FACETS} />}
					/>
				}
			>
				{/* A runtime is not a connector. Under the four-tab bar, two of its
				    panels existed only to say so ("Runtimes are managed as a single
				    sandbox", "This runtime does not expose agent operations") — the
				    clearest sign the tab set never fitted. As sections they are
				    simply absent. */}
				<SandboxOverview sandbox={sandbox} />
				<PageSection title="Settings" muted>
					<SandboxDetail
						sandbox={sandbox}
						onDeleted={() =>
							void navigate({
								to: "/$owner/connectors",
								params: { owner: ownerSlug },
							})
						}
					/>
				</PageSection>
			</ConnectorPageShell>
		</div>
	);
}

/**
 * The provider-specific content for the shared connector detail page. Same
 * shell, header treatment, and tab bar as every connector — only the content
 * slot differs:
 *   - org has this provider → the provider management body (edit / configure /
 *     rotate key / set default; delete under Settings).
 *   - unknown slug that names an inference-catalog kind → the not-yet-connected
 *     body: the create-provider form, prefilled from search params (mirrors the
 *     bundled-but-not-installed connector precedent above).
 *   - neither → the existing not-installed fallback.
 */
function ProviderConnectorBody({
	ownerSlug,
	providerSlug,
}: {
	ownerSlug: string;
	providerSlug: string;
}) {
	const navigate = useNavigate();
	const { model, reason, agentId, addAccount } = Route.useSearch();
	const {
		data: providers = [],
		isLoading: isProvidersLoading,
		isError: isProvidersError,
		refetch: refetchProviders,
	} = useInferenceProviders();
	const catalogQuery = useInferenceProviderCatalog();
	const { data: catalog = [], isLoading: isCatalogLoading } = catalogQuery;

	const provider = addAccount
		? null
		: (providers.find((p) => p.slug === providerSlug) ?? null);
	// Header icon/name join the catalog by the provider's `kind` (its catalog
	// identity — the org-chosen slug may be renamed). Custom kinds miss the
	// catalog and deliberately degrade to slug-only display.
	const catalogItem =
		catalog.find((c) => c.slug === (provider?.kind ?? providerSlug)) ?? null;

	if (isProvidersLoading || isCatalogLoading) {
		// Same reserved-footprint skeleton as the connector branch below.
		return (
			<div>
				<ConnectorPageColumn className="space-y-4">
					<div className="flex items-center gap-2">
						<Skeleton className="h-6 w-6 rounded" />
						<Skeleton className="h-5 w-40" />
					</div>
					<Skeleton className="h-16 w-full rounded-lg" />
					<Skeleton className="h-24 w-full rounded-lg" />
					<Skeleton className="h-32 w-full rounded-lg" />
				</ConnectorPageColumn>
			</div>
		);
	}

	if (isProvidersError)
		return (
			<ConnectorPageColumn>
				<div role="alert">
					<p>Could not load this account.</p>
					<Button variant="outline" onClick={() => void refetchProviders()}>
						Retry
					</Button>
				</div>
			</ConnectorPageColumn>
		);
	if (!provider && !catalogItem && !catalogQuery.isError) {
		// Neither a connected provider nor a catalog kind — the existing
		// not-installed fallback, so a stale link never dead-ends on a blank page.
		return (
			<ConnectorPageColumn className="space-y-3">
				<ConnectorDisplay
					connector={{ key: providerSlug, name: providerSlug }}
					showDescription={false}
					className="gap-2"
				/>
				<p className="text-sm text-muted-foreground">
					This connector isn't installed in this workspace, so its details
					aren't available.
				</p>
			</ConnectorPageColumn>
		);
	}

	const displayName = provider
		? provider.displayName || provider.slug
		: catalogItem?.displayName || providerSlug;
	const isConnected = provider !== null;

	// `?reason` (presence-only — the value is never displayed) marks a link a
	// chat CTA emitted because an agent could not reply; it gets the full
	// explainer. `?model` alone is management targeting: review + auto-open edit.
	const banner = reason ? (
		<AgentNeedsProviderBanner
			agentId={agentId}
			model={model}
			providerName={displayName}
		/>
	) : model ? (
		<ProviderReviewBanner
			providerName={displayName}
			model={model}
			isConnected={isConnected}
		/>
	) : null;

	const hero = (
		<DetailHeader
			breadcrumb={
				<DetailBreadcrumb
					crumbs={[
						<Link
							key="connectors"
							to="/$owner/connectors"
							params={{ owner: ownerSlug }}
							className={DETAIL_CRUMB_LINK_CLASS}
						>
							Connectors
						</Link>,
						...(provider
							? [
									<Link
										key="family"
										to="/$owner/connectors/$connectorKey"
										params={{
											owner: ownerSlug,
											connectorKey: `${INFERENCE_ROW_KEY_PREFIX}${provider.kind}`,
										}}
										search={{ instances: true }}
										className={DETAIL_CRUMB_LINK_CLASS}
									>
										{catalogItem?.displayName ?? provider.kind}
									</Link>,
								]
							: []),
					]}
				/>
			}
			icon={<ConnectorMark iconUrl={catalogItem?.iconUrl} name={displayName} />}
			title={displayName}
			badge={
				provider ? (
					<>
						{provider.isDefault ? (
							<Badge variant="secondary">Default</Badge>
						) : null}
						{provider.status ? (
							<Badge variant="outline">{provider.status}</Badge>
						) : null}
						{provider.hasCustomUpstream ? (
							<Badge variant="outline">Custom upstream</Badge>
						) : null}
					</>
				) : null
			}
			// `status`, not `caption`: chips are a <div> and the caption slot is a
			// <p> (invalid nesting). The slot also puts them on the title row, the
			// same place the connectors list shows them.
			status={<FacetChips facets={INFERENCE_ONLY_FACETS} />}
		/>
	);

	return (
		<div className="flex flex-1 flex-col">
			<ConnectorPageShell header={hero}>
				{banner}
				<div className="space-y-6">
					{provider ? (
						<InferenceProviderDetail
							provider={provider}
							catalog={catalog}
							autoOpenEdit={Boolean(model)}
						/>
					) : (
						<ProviderConnectBody
							ownerSlug={ownerSlug}
							providerSlug={providerSlug}
							existingSlugs={providers.map((item) => item.slug)}
							catalog={catalog}
							catalogError={catalogQuery.isError ? catalogQuery.error : null}
							model={model}
						/>
					)}
				</div>

				{/* A model provider is not a connector either. Its Connections and
				    Actions tabs only ever explained their own absence. Settings
				    appears once there is something to remove. */}
				{provider ? (
					<PageSection title="Settings" muted>
						<InferenceProviderRemoveSection
							provider={provider}
							onDeleted={() =>
								void navigate({
									to: "/$owner/connectors",
									params: { owner: ownerSlug },
								})
							}
						/>
					</PageSection>
				) : null}
			</ConnectorPageShell>
		</div>
	);
}

/** Not-yet-connected body: the create form, prefilled from the search params. */
function ProviderConnectBody({
	ownerSlug,
	providerSlug,
	existingSlugs,
	catalog,
	catalogError,
	model,
}: {
	ownerSlug: string;
	providerSlug: string;
	existingSlugs: string[];
	catalog: InferenceCatalogItem[];
	catalogError: unknown;
	model?: string;
}) {
	const navigate = useNavigate();
	const catalogItem = catalog.find((item) => item.slug === providerSlug);
	// OAuth writes the catalog slug, regardless of the form's editable slug.
	const oauthWouldReplace =
		existingSlugs.includes(providerSlug) &&
		!!catalogItem &&
		supportsOAuth(catalogItem);
	let initialSlug = providerSlug;
	for (let suffix = 2; existingSlugs.includes(initialSlug); suffix++) {
		initialSlug = `${providerSlug}-${suffix}`;
	}
	if (catalogError) {
		return (
			<div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
				<div className="flex gap-2">
					<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
					<div>
						<p className="font-medium">Failed to load provider catalog.</p>
						<p className="mt-1 text-xs">
							{catalogError instanceof Error
								? catalogError.message
								: "Check the workspace URL and try again."}
						</p>
					</div>
				</div>
			</div>
		);
	}
	return (
		<div className="space-y-3 rounded-lg border p-4">
			{oauthWouldReplace && (
				<div className="space-y-2 text-sm">
					<p>Subscription sign-in is managed on the existing connection.</p>
					<Button asChild variant="outline" size="sm">
						<Link
							to="/$owner/connectors/$connectorKey"
							params={{
								owner: ownerSlug,
								connectorKey: `${INFERENCE_ROW_KEY_PREFIX}${providerSlug}`,
							}}
							search={{}}
						>
							Open existing connection
						</Link>
					</Button>
				</div>
			)}
			{/* Keyed on the loaded catalog so the form's initial state seeds from
			    real data (the query resolves after first paint). */}
			{(!oauthWouldReplace ||
				(catalogItem && isApiKeyAddable(catalogItem))) && (
				<CreateProviderForm
					key={`${catalog.length}:${providerSlug}`}
					catalog={catalog}
					initialProvider={providerSlug}
					initialSlug={initialSlug}
					allowOAuth={!oauthWouldReplace}
					initialModel={
						model?.includes("/") ? model.split("/").slice(1).join("/") : model
					}
					onDone={() =>
						void navigate({
							to: "/$owner/connectors",
							params: { owner: ownerSlug },
						})
					}
				/>
			)}
		</div>
	);
}

/** Amber management-targeting banner: a chat CTA pointed here at a model. */
function ProviderReviewBanner({
	providerName,
	model,
	isConnected,
}: {
	providerName: string;
	model: string;
	isConnected: boolean;
}) {
	return (
		<div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
			<div className="flex gap-2">
				<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
				<p>
					Review <strong>{providerName}</strong> for model{" "}
					<strong>{model}</strong>.{" "}
					{isConnected
						? "Its edit controls are open."
						: "It is not connected yet; add it to continue."}
				</p>
			</div>
		</div>
	);
}

/** Agent explainer banner for `?reason` links emitted into chat messages. */
function AgentNeedsProviderBanner({
	agentId,
	model,
	providerName,
}: {
	agentId?: string;
	model?: string;
	providerName: string;
}) {
	return (
		<div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
			<div className="flex gap-2">
				<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
				<div className="space-y-1">
					<p className="font-medium">
						This agent needs a model provider before it can reply.
					</p>
					<p>
						{agentId ? (
							<span>
								Agent <strong>{agentId}</strong> is{" "}
							</span>
						) : null}
						configured to use {model ? <strong>{model}</strong> : "a model"}{" "}
						from <strong>{providerName}</strong>, but that provider is not ready
						in this workspace.
					</p>
					<p>Connect the provider below, then retry the message.</p>
				</div>
			</div>
		</div>
	);
}

function ConnectorBody({
	ownerSlug,
	organizationId,
	isWorkspaceLoading,
	connectorKey,
}: {
	ownerSlug: string;
	organizationId: string | null;
	isWorkspaceLoading: boolean;
	connectorKey: string;
}) {
	// Resolve against the full installable catalog (not just installed defs) so
	// a bundled-but-not-yet-installed connector — e.g. the managed-chat Slack
	// connector reached from the catalog card — still resolves and renders its
	// install affordance instead of the "not installed, details unavailable"
	// dead end.
	const {
		data: connectorDefs = [],
		isLoading: isDefsLoading,
		isError: isDefsError,
		refetch: refetchDefs,
	} = useInstallableConnectorCatalog(organizationId ?? "");
	const {
		data: connections = [],
		isLoading: isConnectionsLoading,
		isError: isConnectionsError,
		refetch: refetchConnections,
	} = useConnections(organizationId ?? "", { connectorKey });
	const { setup } = Route.useSearch();
	const navigate = Route.useNavigate();
	const connector = useMemo(
		() => connectorDefs.find((def) => def.key === connectorKey),
		[connectorDefs, connectorKey],
	);

	// The connection's stored connector_name is the fallback display when no
	// definition is installed (orphaned connector, or a connection created
	// outside the install flow) — same def-or-fallback resolution the Connectors
	// list uses, so the page never dead-ends on a missing definition.
	const display = useMemo(
		() =>
			resolveConnectorDisplay(connectorKey, connectorDefs, {
				name: connections[0]?.connector_name ?? connectorKey,
			}),
		[connectorKey, connectorDefs, connections],
	);

	if (isWorkspaceLoading || isDefsLoading) {
		// Skeleton mirrors `<ConnectorInfo>` shape (connector-panels.tsx:303):
		// icon + name row, description card, auth + feeds + actions panels.
		// Each panel reserves its loaded footprint so the page doesn't
		// shift when data arrives.
		return (
			<div>
				<ConnectorPageColumn className="space-y-4">
					<div className="flex items-center gap-2">
						<Skeleton className="h-6 w-6 rounded" />
						<Skeleton className="h-5 w-40" />
					</div>
					<Skeleton className="h-16 w-full rounded-lg" />
					<Skeleton className="h-24 w-full rounded-lg" />
					<Skeleton className="h-32 w-full rounded-lg" />
				</ConnectorPageColumn>
			</div>
		);
	}

	if (isDefsError || isConnectionsError)
		return (
			<ConnectorPageColumn>
				<div role="alert" className="space-y-2">
					<p>Could not load this connector.</p>
					<Button
						variant="outline"
						onClick={() => {
							void refetchDefs();
							void refetchConnections();
						}}
					>
						Retry
					</Button>
				</div>
			</ConnectorPageColumn>
		);

	const hero = connector ? (
		<ConnectorHero
			connector={connector}
			ownerSlug={ownerSlug}
			organizationId={organizationId ?? undefined}
			connectionCount={connections.length}
		/>
	) : (
		<div className="space-y-3">
			<ConnectorDisplay
				connector={display}
				showDescription={false}
				className="gap-2"
			/>
			<p className="text-sm text-muted-foreground">
				This connector isn't installed in this workspace, so its details aren't
				available.
				{connections.length > 0 ? " Existing connections keep working." : ""}
			</p>
		</div>
	);

	return (
		<div className="flex flex-1 flex-col">
			{connector && organizationId ? (
				<ConnectorDetailShell
					hero={hero}
					connector={connector}
					organizationId={organizationId}
					ownerSlug={ownerSlug}
					connectorKey={connectorKey}
					connections={connections}
					isLoading={isConnectionsLoading}
					openNewApp={setup === "oauth_app"}
					onNewAppHandled={() =>
						void navigate({
							search: (previous) => ({ ...previous, setup: undefined }),
							replace: true,
						})
					}
				/>
			) : (
				<ConnectorPageColumn className="space-y-6">
					{hero}
					{connections.length > 0 && !isConnectionsLoading ? (
						<ConnectorConnectionsBrowser
							section
							connections={connections}
							ownerSlug={ownerSlug}
							connectorKey={connectorKey}
						/>
					) : isConnectionsLoading ? (
						<Skeleton className="h-8 w-40" />
					) : (
						<div>
							<AddConnectionButton
								ownerSlug={ownerSlug}
								connectorKey={connectorKey}
							/>
						</div>
					)}
				</ConnectorPageColumn>
			)}
		</div>
	);
}

/**
 * Sticky header over the connector's sections.
 *
 * This used to describe a tab bar held deliberately stable — tabs were never
 * hidden for a missing capability, because a disappearing tab was the layout
 * shift the bar existed to prevent. That constraint is what made it the wrong
 * control: a connector with no actions got a disabled `Actions` tab whose only
 * content explained its own absence. Sections have no such problem, so one that
 * has nothing to say is simply not rendered.
 */
export function ConnectorDetailShell({
	hero,
	connector,
	organizationId,
	ownerSlug,
	connectorKey,
	connections,
	isLoading,
	openNewApp,
	onNewAppHandled,
}: {
	hero: React.ReactNode;
	connector: ConnectorDefinitionItem;
	organizationId: string;
	ownerSlug: string;
	connectorKey: string;
	connections: ConnectionItem[];
	isLoading: boolean;
	openNewApp?: boolean;
	onNewAppHandled?: () => void;
}) {
	const { isAdmin, isLoading: isRoleLoading } = useMembers(organizationId);
	const hasActions = (connector.operations_summary?.total ?? 0) > 0;
	// Every way to connect lives behind this one control: it leads the page
	// while there is no connection, then heads the connections list.
	const connect = (
		<ConnectMenu
			connector={connector}
			organizationId={organizationId}
			ownerSlug={ownerSlug}
			connections={connections}
			openNewApp={openNewApp}
			onNewAppHandled={onNewAppHandled}
		/>
	);
	return (
		<ConnectorPageShell header={hero}>
			{isLoading ? (
				<Skeleton className="h-8 w-40" />
			) : connections.length > 0 ? (
				<ConnectorConnectionsBrowser
					section
					connections={connections}
					connector={connector}
					ownerSlug={ownerSlug}
					connectorKey={connectorKey}
					action={connect}
				/>
			) : (
				connect
			)}

			<div className="grid grid-cols-1 gap-3 sm:auto-cols-fr sm:grid-flow-col">
				<AvailableFeedsSection
					connector={connector}
					organizationId={organizationId}
					ownerSlug={ownerSlug}
				/>

				{/* A chat connector genuinely has no operations, so this section is
			    absent rather than an empty panel explaining itself. The admin case
			    is different — the operations exist and are being withheld — so it
			    still renders and says why. */}
				{hasActions && isRoleLoading ? (
					<Skeleton className="h-20 w-full rounded-lg" />
				) : hasActions && isAdmin ? (
					<ConnectorActionsSection
						connector={connector}
						organizationId={organizationId}
						ownerSlug={ownerSlug}
					/>
				) : hasActions && !isAdmin ? (
					<PageSection title="Actions" muted>
						<p className="text-sm text-muted-foreground">
							Only workspace admins can view and configure connector actions.
						</p>
					</PageSection>
				) : null}
			</div>

			{/* Connector-level Settings: workspace auth, definition metadata, and
			    uninstall. Connection Settings is the same idea one level down. Only
			    rendered when there is something to configure — a connector with no
			    credentials and nothing to remove hides the whole section. */}
			<ConnectorSetup
				connector={connector}
				organizationId={organizationId}
				ownerSlug={ownerSlug}
			/>
		</ConnectorPageShell>
	);
}

export function ConnectorHero({
	connector,
	ownerSlug,
	organizationId,
	connectionCount,
}: {
	connector: ConnectorDefinitionItem;
	ownerSlug: string;
	organizationId?: string;
	connectionCount: number;
}) {
	return (
		<DetailHeader
			breadcrumb={
				<DetailBreadcrumb
					crumbs={[
						<Link
							key="connectors"
							to="/$owner/connectors"
							params={{ owner: ownerSlug }}
							className={DETAIL_CRUMB_LINK_CLASS}
						>
							Connectors
						</Link>,
					]}
				/>
			}
			icon={
				<ConnectorMark
					icon={connector.icon}
					name={connector.name}
					faviconDomain={
						connector.favicon_domain ??
						extractOAuthDomain(connector.auth_schema)
					}
					className="h-10 w-10"
				/>
			}
			title={connector.name}
			badge={
				<>
					<ConnectorVersionBadge
						connectorDef={connector}
						ownerSlug={ownerSlug}
					/>
					{organizationId ? (
						<ConnectorRemoveButton
							connector={connector}
							organizationId={organizationId}
							ownerSlug={ownerSlug}
							connectionCount={connectionCount}
						/>
					) : null}
				</>
			}
			caption={connector.description}
			meta={
				<RunActivityLink
					owner={ownerSlug}
					filters={{ connectorKey: connector.key }}
					name={connector.name}
				/>
			}
		/>
	);
}

function ConnectorSetup({
	connector,
	organizationId,
	ownerSlug,
}: {
	connector: ConnectorDefinitionItem;
	organizationId: string;
	ownerSlug: string;
}) {
	const methods = getSelectableMethods(connector.auth_schema);
	const allMethods = getAuthMethods(connector.auth_schema);
	const oauthMethod = getOAuthMethod(connector.auth_schema);
	const appInstall = getAppInstallMethod(connector.auth_schema);
	const hasEnv = methods.some((m) => m.type === "env_keys");
	const hasBrowser = methods.some((m) => m.type === "browser");
	const hasInteractive = allMethods.some((m) => m.type === "interactive");
	const hasWorkspaceAuth = Boolean(
		oauthMethod || appInstall || hasEnv || hasBrowser,
	);
	const isDeviceBound = connectorIsDeviceBound(connector);

	// Profile presence decides whether a credential section exists at all — an
	// env/browser connector with no configured credentials has nothing to show.
	const { data: envProfiles = [] } = useAuthProfiles(organizationId, {
		connectorKey: connector.key,
		profileKind: "env",
	});
	const { data: browserProfiles = [] } = useAuthProfiles(organizationId, {
		profileKind: "browser_session",
		connectorKey: connector.key,
	});

	const hasAnything =
		(hasEnv && envProfiles.length > 0) ||
		(hasBrowser && browserProfiles.length > 0) ||
		// A connector with no credentials at all has nothing to say here; device
		// and pairing connectors still explain where their credentials live.
		(!hasWorkspaceAuth && (isDeviceBound || hasInteractive));
	if (!hasAnything) return null;

	return (
		<div className="space-y-6">
			{hasEnv && envProfiles.length > 0 ? (
				<CredentialSummary profiles={envProfiles} type="env" />
			) : null}
			{hasBrowser && browserProfiles.length > 0 ? (
				<CredentialSummary profiles={browserProfiles} type="browser_session" />
			) : null}
			{/* Device-bridged and interactive connectors have no workspace
				    credentials — still say why the auth block is quiet. */}
			{!hasWorkspaceAuth ? (
				isDeviceBound ? (
					<PageSection title="Device runtime">
						<div className="space-y-3">
							<p className="text-sm text-muted-foreground">
								{connector.name} runs on a linked device — there are no
								workspace credentials to configure here. Pair a device, then
								create a connection pinned to it.
							</p>
							<Button asChild variant="outline" size="sm">
								<Link
									to="/$owner/connectors"
									params={{ owner: ownerSlug }}
									search={{}}
								>
									View devices
								</Link>
							</Button>
						</div>
					</PageSection>
				) : hasInteractive ? (
					<PageSection title="Authentication">
						<p className="text-sm text-muted-foreground">
							{connector.name} pairs when you create a connection — there is no
							workspace-level credential to configure here.
						</p>
					</PageSection>
				) : null
			) : null}
		</div>
	);
}

/** Uninstall, as an icon beside the version: a rare admin action needs no section of its own. */
export function ConnectorRemoveButton({
	connector,
	organizationId,
	ownerSlug,
	connectionCount,
}: {
	connector: ConnectorDefinitionItem;
	organizationId: string;
	ownerSlug: string;
	connectionCount: number;
}) {
	const navigate = useNavigate();
	const { isAdmin } = useMembers(organizationId);
	const uninstall = useUninstallConnector();
	const [error, setError] = useState<string | null>(null);

	// Catalog-only preview rows (not installed in the org) cannot be uninstalled.
	const isInstalled = connector.installed !== false;
	if (!isAdmin || !isInstalled) return null;

	const blockedByConnections = connectionCount > 0;
	const busy = uninstall.isPending;

	const handleRemove = () => {
		setError(null);
		uninstall.mutate(
			{ connector_key: connector.key },
			{
				onSuccess: () => {
					void navigate({
						to: "/$owner/connectors",
						params: { owner: ownerSlug },
					});
				},
				onError: (err) => {
					setError(
						err instanceof Error ? err.message : "Failed to remove connector",
					);
				},
			},
		);
	};

	return (
		<Popover onOpenChange={() => setError(null)}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="h-7 w-7 text-muted-foreground hover:text-destructive"
					aria-label="Remove connector"
					title="Remove connector"
				>
					<Trash2 className="h-3.5 w-3.5" />
				</Button>
			</PopoverTrigger>
			<PopoverContent align="start" className="space-y-3">
				<p className="text-sm font-medium">Remove {connector.name}?</p>
				<p className="text-xs text-muted-foreground">
					Archives it in this workspace. Existing synced data stays in memory.
				</p>
				{blockedByConnections ? (
					<p className="text-xs text-muted-foreground">
						Delete{" "}
						<span className="font-medium text-foreground">
							{connectionCount} connection{connectionCount === 1 ? "" : "s"}
						</span>{" "}
						first — uninstall is blocked while any connection still references
						this connector.
					</p>
				) : null}
				{error ? (
					<p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
						{error}
					</p>
				) : null}
				<Button
					variant="destructive"
					size="sm"
					disabled={busy || blockedByConnections}
					onClick={handleRemove}
				>
					{busy ? <Spinner size="md" className="mr-1.5" /> : null}
					Remove connector
				</Button>
			</PopoverContent>
		</Popover>
	);
}

function ConnectorActionsSection({
	connector,
	organizationId,
	ownerSlug,
}: {
	connector: ConnectorDefinitionItem;
	organizationId: string;
	ownerSlug: string;
}) {
	const peek = useNotificationPeek();
	return (
		<CapabilitySummary
			title="Actions"
			summary={`${connector.operations_summary?.total ?? 0} actions · organization policies`}
			onOpen={() =>
				peek?.open({
					href: "",
					title: "Actions",
					connectorPanel: {
						section: "actions",
						connectorKey: connector.key,
						organizationId,
						ownerSlug,
					},
				})
			}
		/>
	);
}

function CredentialSummary({
	profiles,
	type,
}: {
	profiles: AuthProfileItem[];
	type: "env" | "browser_session";
}) {
	const title = type === "env" ? "Credentials" : "Browser sessions";
	return (
		<PageSection title={title}>
			<div className="space-y-2">
				{profiles.map((profile) => (
					<div key={profile.slug} className={cn(FILLED_BOX, "p-3")}>
						<div className="flex flex-wrap items-center gap-2">
							<span className="text-sm font-medium">
								{profile.display_name}
							</span>
							<AuthStatusBadge status={profile.status} />
						</div>
						<p className="truncate text-xs text-muted-foreground">
							{profile.slug}
						</p>
					</div>
				))}
			</div>
		</PageSection>
	);
}

/**
 * What this connector can bring in, from its `feeds_schema`. The connector page
 * is the only surface that renders it: a connection shows the feeds you HAVE,
 * this shows the ones the connector OFFERS, which is what you want before
 * deciding to connect it at all.
 */
function AvailableFeedsSection({
	connector,
	organizationId,
	ownerSlug,
}: {
	connector: ConnectorDefinitionItem;
	organizationId: string;
	ownerSlug: string;
}) {
	const peek = useNotificationPeek();
	const count = Object.keys(connector.feeds_schema ?? {}).length;
	if (!count) return null;
	return (
		<CapabilitySummary
			title="Feeds"
			summary={`${count} ${count === 1 ? "feed type" : "feed types"}`}
			onOpen={() =>
				peek?.open({
					href: "",
					title: "Feeds",
					connectorPanel: {
						section: "feeds",
						connectorKey: connector.key,
						organizationId,
						ownerSlug,
					},
				})
			}
		/>
	);
}
